// Takvimim — saf takvim modeli (DOM'suz). Uç yanıtını (GET /v1/my-calendar) gün dizinine çevirir; hücre
// katmanı, şerit birleştirme, lejant sayıları, tatil bloğu ve onay satırları buradan gelir. Mobil
// (mobile/src/utils/calendarModel.js) bu dosyanın birebir portudur — davranış iki yerde aynı kalmalı.
//
// Tarihler her yerde "yyyy-MM-dd" yerel gün anahtarıdır; sunucu kişinin yerel gününü gönderir, burada
// saat dilimi dönüşümü YAPILMAZ. Hafta Pazartesi başlar (dow 0 = Pazartesi).

export const YEAR_WINDOW = 5;

const DAY_MS = 24 * 60 * 60 * 1000;
const TR_OFFSET_MS = 3 * 60 * 60 * 1000;   // Türkiye duvar saati: sabit UTC+3, yaz saati yok

const pad2 = (n) => String(n).padStart(2, '0');

export const dateKey = (year, monthIndex, day) => `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;

const keyToUtc = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

const utcToKey = (ms) => new Date(ms).toISOString().slice(0, 10);

export const addDaysKey = (key, n) => utcToKey(keyToUtc(key) + n * DAY_MS);

export const daysBetween = (fromKey, toKey) => Math.round((keyToUtc(toKey) - keyToUtc(fromKey)) / DAY_MS);

// 0 = Pazartesi … 6 = Pazar
export const dowOf = (key) => (new Date(keyToUtc(key)).getUTCDay() + 6) % 7;

// "Bugün" Türkiye duvar saatine göre (sunucunun TrZaman kuralıyla aynı).
export function todayInIstanbul(now = new Date()) {
  return utcToKey(now.getTime() + TR_OFFSET_MS);
}

/**
 * Gün dizini: date → { holiday, leaves: [{ leave, weight }], hourly: [leave] }.
 * Günlük izin her günü için ağırlığıyla, saatlik izin tek gününe eklenir.
 */
export function buildDayIndex(data) {
  const index = new Map();
  const entry = (date) => {
    let e = index.get(date);
    if (!e) {
      e = { holiday: null, leaves: [], hourly: [] };
      index.set(date, e);
    }
    return e;
  };

  (data?.holidays || []).forEach((h) => { entry(h.date).holiday = h; });
  (data?.leaves || []).forEach((leave) => {
    (leave.days || []).forEach((d) => {
      if (leave.isHourly) entry(d.date).hourly.push(leave);
      else entry(d.date).leaves.push({ leave, weight: Number(d.weight) });
    });
  });
  return index;
}

const STATUS_RANK = { approved: 0, waiting: 1 };

// Öncelik: onaylı > bekleyen, sonra erken başlayan (spec §5.7).
const byLeavePriority = (a, b) => (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9)
  || String(a.startDate).localeCompare(String(b.startDate))
  || String(a.startTime || '').localeCompare(String(b.startTime || ''))
  || String(a.id).localeCompare(String(b.id));

const EMPTY_CELL = Object.freeze({
  kind: null, groupId: null, soft: false, half: false, holidayDot: false, hourlyDot: null, leaves: [], holiday: null,
});

/**
 * Bir günün görünür katmanı. visible = { holiday, approved, waiting }.
 * Görünür günlük izin > görünür tatil > boş; saatlik izin şerit çizmez, nokta koyar (spec §7.2).
 */
export function cellFor(entry, visible) {
  if (!entry) return EMPTY_CELL;

  const holiday = visible.holiday ? entry.holiday : null;
  const daily = entry.leaves
    .filter((x) => visible[x.leave.status])
    .sort((a, b) => byLeavePriority(a.leave, b.leave));
  const hourly = entry.hourly.filter((l) => visible[l.status]).sort(byLeavePriority);

  const cell = {
    ...EMPTY_CELL,
    holiday,
    leaves: [...daily.map((x) => x.leave), ...hourly],
    hourlyDot: hourly.length ? hourly[0].status : null,
  };

  if (daily.length) {
    const top = daily[0];
    cell.kind = top.leave.status;
    cell.groupId = top.leave.id;
    cell.soft = top.weight === 0;
    cell.half = top.weight === 0.5;
    cell.holidayDot = !!holiday;
  } else if (holiday) {
    cell.kind = 'holiday';
    cell.half = !!holiday.isHalfDay;
    cell.groupId = holiday.isHalfDay ? `h:${holiday.date}` : `hc:${holiday.calendarId}`;
  }
  return cell;
}

/**
 * Ay ızgarası: baştaki boşluklar null; her gün { date, day, dow, isWeekend, cell, joinLeft, joinRight }.
 * Şerit yalnız aynı hafta satırında, komşu aynı kind + groupId ise birleşir; yarım gün hiç birleşmez.
 */
export function monthCells(year, monthIndex, index, visible) {
  const lead = (new Date(Date.UTC(year, monthIndex, 1)).getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

  const days = [];
  for (let d = 1; d <= daysInMonth; d += 1) {
    const date = dateKey(year, monthIndex, d);
    const dow = (lead + d - 1) % 7;
    days.push({
      date, day: d, dow, isWeekend: dow >= 5, cell: cellFor(index.get(date), visible), joinLeft: false, joinRight: false,
    });
  }

  const joins = (a, b) => !!a && !!b && !!a.cell.kind && a.cell.kind === b.cell.kind
    && a.cell.groupId === b.cell.groupId && !a.cell.half && !b.cell.half;
  days.forEach((c, i) => {
    c.joinLeft = c.dow > 0 && joins(c, days[i - 1]);
    c.joinRight = c.dow < 6 && joins(c, days[i + 1]);
  });

  return [...Array(lead).fill(null), ...days];
}

/**
 * Lejant sayıları (spec §5.4): tatil günü (yarım 0,5); günlük izinlerde YIL İÇİNE düşen ağırlıklar;
 * saatlik izinlerde saklı maliyet (saat).
 */
export function yearTotals(data, year) {
  const prefix = `${year}-`;
  const totals = { holidayDays: 0, approvedDays: 0, approvedHours: 0, waitingDays: 0, waitingHours: 0 };

  (data?.holidays || []).forEach((h) => {
    if (h.date.startsWith(prefix)) totals.holidayDays += h.isHalfDay ? 0.5 : 1;
  });
  (data?.leaves || []).forEach((l) => {
    if (l.status !== 'approved' && l.status !== 'waiting') return;
    if (l.isHourly) {
      const day = l.days?.[0]?.date || l.startDate;
      if (day && day.startsWith(prefix)) totals[`${l.status}Hours`] += Number(l.cost) || 0;
      return;
    }
    totals[`${l.status}Days`] += (l.days || [])
      .filter((d) => d.date.startsWith(prefix))
      .reduce((sum, d) => sum + Number(d.weight), 0);
  });
  return totals;
}

/**
 * Tıklanan tatilin görsel bloğu: aynı takvimden art arda TAM GÜN tatiller (≥ 2 gün). Yarım gün/tek gün → null.
 * Kart tıklanan günün kaydını anlatır; blok yalnız ikincil satırdır (spec §5.6).
 */
export function holidayBlock(index, date) {
  const h = index.get(date)?.holiday;
  if (!h || h.isHalfDay) return null;
  const same = (key) => {
    const o = index.get(key)?.holiday;
    return !!o && !o.isHalfDay && o.calendarId === h.calendarId;
  };
  let start = date;
  let end = date;
  while (same(addDaysKey(start, -1))) start = addDaysKey(start, -1);
  while (same(addDaysKey(end, 1))) end = addDaysKey(end, 1);
  const days = daysBetween(start, end) + 1;
  return days >= 2 ? { start, end, days } : null;
}

const stepName = (s) => s.approverName || s.approverTitle || s.approverCode || null;

/**
 * Kartın onay satırları (spec §13.3). flowSteps = kişinin bugünkü izin akışı adımları (yoksa []).
 * - Geçmiş varsa: geçmiş satırları (tarihli) + bekleyen izinde geçmişin en büyük adımından sonraki akış adımları.
 * - Geçmiş yoksa: akış adımlarından türetilir (ApprovalFlowTimeline kuralı), tarihsiz.
 * - Akış da yoksa: bekleyende tek "waiting" satırı (CurrentApproverName), onaylıda boş.
 * Bekleyen izinde hiçbir satır "waiting" değilse ve onaycı adı biliniyorsa sona o eklenir (akış dışı yönlendirme).
 */
export function approvalRows(leave, flowSteps) {
  const steps = (flowSteps || []).slice().sort((a, b) => a.stepOrder - b.stepOrder);
  const history = leave.history || [];
  const rows = [];

  if (history.length) {
    // Geçmiş kaydı başlamadan önce verilmiş adım onayları (kayıt öncesi uçuştaki izin): geçmişin ilk adımından önceki
    // akış adımları onaylanmış sayılır, tarihsiz.
    const recorded = history.map((h) => h.stepOrder).filter((o) => o != null);
    const firstRecorded = recorded.length ? Math.min(...recorded) : null;
    if (firstRecorded != null) {
      steps.filter((s) => s.stepOrder < firstRecorded).forEach((s) => rows.push({
        key: `p${s.stepOrder}`,
        name: stepName(s),
        photo: s.approverProfilePicture || null,
        title: s.approverType === 'position' ? s.approverTitle || null : null,
        state: 'approved',
        date: null,
        source: null,
      }));
    }
    history.forEach((h, i) => {
      const step = h.stepOrder != null ? steps.find((s) => s.stepOrder === h.stepOrder) : null;
      rows.push({
        key: `h${i}`,
        name: h.actorName || (step ? stepName(step) : null),
        photo: h.actorProfilePicture || (step ? step.approverProfilePicture || null : null),
        title: h.actorTitle || (step && step.approverType === 'position' ? step.approverTitle || null : null),
        // selfSkipped: talep sahibinin kendi adımı olduğu için atlandı (spec 2026-11-01 §4.7) — kişisiz, tarihsiz gösterim.
        state: h.action === 'rejected' ? 'rejected' : (h.action === 'selfSkipped' ? 'skipped' : 'approved'),
        date: h.action === 'selfSkipped' ? null : h.actionDate,
        source: h.action === 'selfSkipped' ? null : h.source,
      });
    });
    if (leave.status === 'waiting') {
      const maxOrder = Math.max(0, ...history.map((h) => h.stepOrder || 0));
      steps.filter((s) => s.stepOrder > maxOrder).forEach((s) => rows.push({
        key: `s${s.stepOrder}`,
        name: stepName(s),
        photo: s.approverProfilePicture || null,
        title: s.approverType === 'position' ? s.approverTitle || null : null,
        state: s.approverId === leave.currentApprover ? 'waiting' : 'next',
        date: null,
        source: null,
      }));
    }
  } else if (steps.length) {
    const current = steps.find((s) => s.approverId === leave.currentApprover);
    steps.forEach((s) => {
      let state = 'next';
      if (leave.status === 'approved') state = 'approved';
      else if (s.approverId === leave.currentApprover) state = 'waiting';
      else if (current && s.stepOrder < current.stepOrder) state = 'approved';
      rows.push({
        key: `s${s.stepOrder}`,
        name: stepName(s),
        photo: s.approverProfilePicture || null,
        title: s.approverType === 'position' ? s.approverTitle || null : null,
        state,
        date: null,
        source: null,
      });
    });
  }

  if (leave.status === 'waiting' && !rows.some((r) => r.state === 'waiting') && leave.currentApproverName) {
    rows.push({
      key: 'current', name: leave.currentApproverName, photo: null, title: null, state: 'waiting', date: null, source: null,
    });
  }
  return rows;
}

// İzin aralığında izinden düşmeyen günler (yalnız kırpılmış aralıktaki 0 ağırlıklar, spec §5.3): sunucunun gün başına
// `isHoliday` işaretiyle tatil olanlar "resmî tatil", kalanlar "izin dışı gün" (yıl sınırını aşan izinde öbür yılın
// tatilleri yanıtın tatil listesinde olmadığı için işaret sunucudan gelir).
export function notDeductedCounts(leave) {
  if (leave.isHourly) return { offDays: 0, holidays: 0 };
  let offDays = 0;
  let holidays = 0;
  (leave.days || []).forEach((d) => {
    if (Number(d.weight) !== 0) return;
    if (d.isHoliday) holidays += 1;
    else offDays += 1;
  });
  return { offDays, holidays };
}
